import { Conditions } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { AppBreadCrumbTemplate } from '@/app/AppBreadCrumbTemplate';
import { Account } from '@/client/accounts';
import { EmailTemplate } from '@/client/email-templates';
import { ThumbnailState } from '@/client/thumbnails/enums';
import { Actions, Subjects } from '@/client/users';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { TemplatesFilters } from '@/components/templates';
import ThumbnailsGenerationNotify from '@/components/thumbnails/ThumbnailsGenerationNotify';
import { useDeleteEmailTemplate, useEmailTemplates } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { useTemplatesFilters } from '@/hooks/templates';
import { usePermission } from '@/hooks/usePermission';
import { useToast } from '@/hooks/useToast';
import { EmailTemplatesTabs } from '@/pages/email-templates';
import { selectCurrentAccount } from '@/store/features/account';
import { DialogType } from '@/system-settings/enums';
import { AppBreadCrumb } from '@/ui/breadcrumb';
import { AppButton } from '@/ui/buttons';
import { ImageCard } from '@/ui/image-card';
import { CardGridContainer, FlexContainer } from '@/ui/styled-ui';
import { displayCreatedBy } from '@/utils/helpers';
import { AxiosError } from 'axios';
import { MenuItem } from 'primereact/menuitem';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const EmailTemplatesCustomPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { setDialogData } = useContext(DialogContext);
  const account = useAppSelector(selectCurrentAccount);
  const deleteEmailTemplate = useDeleteEmailTemplate();
  const { can } = usePermission();
  const {
    take,
    nameFilter,
    search,
    skip,
    handleSearch,
    handleChangeSkip,
    handleChangeDefaultRows,
  } = useTemplatesFilters();

  const breadCrumbItems = [
    {
      label: (account as Account)?.name,
      url: !account?.isSystem
        ? RedirectPaths[RedirectPathsEnum.ACCOUNT](account?.id as string)
        : RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account?.id),
      template: AppBreadCrumbTemplate,
    },
    {
      label: t('templates.custom.email'),
      url: RedirectPaths[RedirectPathsEnum.EMAIL_TEMPLATES_CUSTOM](),
      template: AppBreadCrumbTemplate,
    },
  ];

  // Templates filtered by account, will return only custom templates
  const { isLoading, emailTemplates, refetch } = useEmailTemplates({
    take,
    skip,
    filters: [
      {
        field: 'account',
        condition: Conditions.EQUAL,
        value: (account as Account)?.id,
      },
      ...nameFilter,
    ],
  });

  const handleDeleteEmailTemplate = async (emailTemplateId: string) => {
    try {
      await deleteEmailTemplate.delete(emailTemplateId);
      setTimeout(() => {
        refetch();
      }, 100);
      toast?.success(t('toast.success'), t('templates.custom.email.delete'));
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }
  };

  const menuItems = (emailTemplate: EmailTemplate) => {
    const actions: MenuItem[] = [];
    if (can(Actions.CREATE, Subjects.EMAIL_TEMPLATES)) {
      actions.push({
        label: t('generic.copyEdit'),
        icon: 'pi pi-copy',
        command: () =>
          navigate(RedirectPaths[RedirectPathsEnum.EMAIL_TEMPLATES_CREATE](), {
            state: { emailTemplate },
          }),
      });
    }

    if (can(Actions.UPDATE, Subjects.EMAIL_TEMPLATES)) {
      actions.push({
        label: t('generic.edit'),
        icon: 'pi pi-pencil',
        command: () =>
          navigate(
            RedirectPaths[RedirectPathsEnum.EMAIL_TEMPLATES_UPDATE](
              emailTemplate.id,
            ),
          ),
      });
    }

    if (can(Actions.DELETE, Subjects.EMAIL_TEMPLATES)) {
      actions.push({
        label: t('generic.delete'),
        icon: 'pi pi-times',
        command: () =>
          setDialogData({
            type: DialogType.CONFIRMATION,
            show: true,
            header: t('dialog.delete.confirm'),
            message: t('dialog.delete.custom.email'),
            onAccept: async () =>
              await handleDeleteEmailTemplate(emailTemplate.id),
          }),
      });
    }

    return actions;
  };

  return (
    <>
      <AppBreadCrumb model={breadCrumbItems} />
      <FlexContainer justify="space-between">
        <h1>{t('templates.custom.email')}</h1>
        {can(Actions.CREATE, Subjects.EMAIL_TEMPLATES) && (
          <AppButton
            label={t('button.createNew')}
            severity="secondary"
            onClick={() => {
              navigate(
                RedirectPaths[RedirectPathsEnum.EMAIL_TEMPLATES_CREATE](),
              );
            }}
          />
        )}
      </FlexContainer>
      <EmailTemplatesTabs />

      <TemplatesFilters
        count={emailTemplates?.count ?? 0}
        take={take}
        skip={skip}
        onPageChange={handleChangeSkip}
        onRowsChange={handleChangeDefaultRows}
        onSearch={handleSearch}
        search={search}
      />
      {!isLoading && !emailTemplates?.result.length && (
        <FlexContainer direction="column" className="mt-5">
          <h3>{t('templates.email.empty')}</h3>
        </FlexContainer>
      )}
      {isLoading && !emailTemplates && (
        <FlexContainer direction="column" className="mt-5">
          <ProgressSpinner />
          <h3>{t('templates.email.loading')}</h3>
        </FlexContainer>
      )}
      {!isLoading && !!emailTemplates?.result.length && (
        <>
          <ThumbnailsGenerationNotify
            refetch={refetch}
            templates={emailTemplates.result}
          />
          <CardGridContainer>
            {emailTemplates.result.map((template) => (
              <ImageCard
                key={template.id}
                id={template.id}
                title={template.name}
                thumbnail={template.thumbnail?.signedUrl}
                isLoading={
                  template.thumbnailState === ThumbnailState.PROCESSING
                }
                author={displayCreatedBy(t, template.createdBy)}
                templateType="email"
                interactionType="withActions"
                actions={menuItems(template)}
              />
            ))}
          </CardGridContainer>
        </>
      )}
    </>
  );
};
